import { deviceApi } from '@/api'
import { getTenantForApiCall, getTenantFromUrl } from '@/api/axiosInstance'
import {
	type Device,
	type DeviceFilterOption,
	type DeviceSearchFilters,
	type DeviceSearchSortBy,
	DeviceStatus,
	type SortOrder,
} from '@/api/deviceApi'
import {
	DEFAULT_DEVICE_VIEW,
	type DeviceView,
	deviceViewRequest,
	resolveDeviceListRequest,
} from '@/app/pages/DeviceManagement/deviceView'
import type { FilterState } from '@/components/filterable-search-bar'
import { buildDeviceSearchFilters } from '@/components/filterable-search-bar/DeviceFiltersUtils'
import { queryClient } from '@/lib/queryClient'
import { deviceKeys } from '@/lib/queryKeys'
import { isPageBeyondTotal } from '@/utils/deviceFieldUtils'
import { type PageError, extractErrorMessage, isAbortError, toPageError } from '@/utils/errorHandler'
import { create } from 'zustand'

// Dedup identical in-flight list requests. On mount the status-sync effect and the
// initial-fetch effect both call fetchDevices() concurrently (and StrictMode doubles
// each); without this they fire several identical /devices requests. Mirrors orgDeviceStore.
const inFlightDeviceFetches = new Set<string>()

/** Matches the other schema reads. The backend caches this response for 15 minutes of its own. */
const DEVICE_FILTER_OPTIONS_STALE_TIME_MS = 5 * 60 * 1000

// Latest-wins: abort the prior list request so a stale poll can't clobber the new page.
// Shared across fetchDevices + searchDevices.
let deviceListAbortController: AbortController | null = null

// Collapse overlapping tab-count refreshes into one.
let refreshTabCountsInFlight = false

interface DeviceStore {
	// Data
	devices: Device[]
	searchFilters: FilterState[]
	// Prebuilt OR-capable filter driven by the grouped filter bar. When set it is the source of truth for
	// the active search (pagination/sort/polling prefer it); the flat searchFilters path stays for callers
	// that still pass FilterState[] (e.g. the dashboards device table).
	searchQuery: DeviceSearchFilters | null
	totalCount: number
	tabCounts: { active: number; inactive: number; all: number }
	deviceFilterOptions: DeviceFilterOption[]

	// Display properties (persisted)
	/** Dashboard links shown per device row. Not a table column, so kept out of `columns`. */
	dashboards: string[]
	/** Non-frozen table columns, namespaced "metadata::<id>" / "state::<id>", or a bare id for a fixed
	 * column. Array position is the persisted order, so there is no columnOrder field. */
	columns: string[]
	/** What the list shows: unarchived only, archived only, or both. */
	deviceView: DeviceView

	// Pagination & Filters
	currentPage: number
	pageSize: number
	statusFilter: DeviceStatus

	// Server-side sort. Project level sorts by 'id' (the serial-key/VIN alias) by default.
	sortBy: DeviceSearchSortBy
	sortOrder: SortOrder

	// Loading & Error States
	isLoading: boolean
	isLoadingDeviceOptions: boolean
	error: PageError | null
	deviceOptionsError: string | null

	/** `status` defaults to the store's `statusFilter`; pass it when the caller's status is newer than the store's. */
	fetchDevices: (silent?: boolean, status?: DeviceStatus) => Promise<void>
	refreshTabCounts: () => Promise<void>
	setPage: (page: number) => void
	setPageSize: (size: number) => void

	setStatusFilter: (status: DeviceStatus) => void
	setSort: (sortBy: DeviceSearchSortBy, sortOrder: SortOrder) => void
	/** The project (or organization) the current sort was restored for. */
	sortScope: string | null
	restoreSort: (sortBy: DeviceSearchSortBy, sortOrder: SortOrder, scope: string) => void
	fetchDeviceFilterOptions: (tenantId?: string, status?: DeviceStatus) => Promise<void>
	clearError: () => void
	clearDeviceOptionsError: () => void
	searchDevices: (
		filters: FilterState[],
		page?: number,
		pageSize?: number,
		status?: DeviceStatus,
		silent?: boolean,
	) => Promise<void>
	setSearchFilters: (filters: FilterState[]) => void
	/** Run the search from a prebuilt OR-capable filter (grouped bar). Pass null to clear and restore the
	 *  unfiltered list. */
	applySearchQuery: (
		query: DeviceSearchFilters | null,
		page?: number,
		pageSize?: number,
		status?: DeviceStatus,
		silent?: boolean,
	) => Promise<void>
	setDashboards: (ids: string[]) => void
	setColumns: (columns: string[]) => void
	setDeviceView: (view: DeviceView) => void
	resetPagination: () => void
	totalPages: number
}

export const useDeviceStore = create<DeviceStore>()((set, get) => {
	// Shared body of searchDevices/applySearchQuery: dedup, abort, fetch the current page. `patch`
	// records which source of truth (flat searchFilters vs grouped searchQuery) owns the active view;
	// `keySeed` namespaces the dedup key.
	const runSearch = async (opts: {
		query: DeviceSearchFilters | null
		keySeed: string
		patch: Partial<DeviceStore>
		filtersChanged: boolean
		page?: number
		pageSize?: number
		status?: DeviceStatus
		silent?: boolean
	}) => {
		if (!getTenantFromUrl()) {
			return
		}

		const { currentPage, pageSize: storePageSize, statusFilter, sortBy, sortOrder, deviceView } = get()
		const effectivePage = opts.page || currentPage
		const effectivePageSize = opts.pageSize || storePageSize
		const effectiveStatus = opts.status || statusFilter
		const request = resolveDeviceListRequest(effectiveStatus, deviceView)

		const requestKey = `${opts.keySeed}:${effectivePage}:${effectivePageSize}:${request.status}:${request.includeArchived}:${JSON.stringify(sortBy)}:${sortOrder}`
		if (inFlightDeviceFetches.has(requestKey)) {
			return
		}
		inFlightDeviceFetches.add(requestKey)

		// Abort prior list request — latest-wins.
		deviceListAbortController?.abort()
		deviceListAbortController = new AbortController()
		const signal = deviceListAbortController.signal

		// Silent refresh (auto-poll) re-runs the active search without flashing loading. Clear the error
		// only when the filter set itself changed — not on polling re-fires of the same failing filter.
		set({
			...opts.patch,
			...(opts.silent ? {} : { isLoading: true }),
			...(opts.filtersChanged ? { error: null } : {}),
		})

		try {
			// `=== null`, not falsiness: `DeviceSearchFilters` admits a bare `false` (match nothing), which
			// getDevicesV3 already forwards under this same predicate. Only "no query at all" is unfiltered.
			if (opts.query === null) {
				// The status is forwarded, not left to the store: the grouped bar's clearing dispatch commits
				// before the page's status-sync effect (child before parent), so on a tab switch the store still
				// holds the outgoing tab's status while `opts.status` already carries the incoming one.
				await get().fetchDevices(opts.silent, opts.status)
				return
			}

			const response = await deviceApi.getDevicesV3(
				effectivePage,
				effectivePageSize,
				request.status,
				undefined,
				opts.query,
				sortBy,
				sortOrder,
				signal,
				request.includeArchived,
			)
			const calculatedTotalPages = response.count > 0 ? Math.ceil(response.count / effectivePageSize) : 1

			if (isPageBeyondTotal(effectivePage, response.count, calculatedTotalPages)) {
				inFlightDeviceFetches.delete(requestKey)
				set({ currentPage: calculatedTotalPages })
				await runSearch({ ...opts, page: calculatedTotalPages })
				return
			}

			set({
				devices: response.devices,
				totalCount: response.count,
				isLoading: false,
				totalPages: calculatedTotalPages,
				error: null,
			})
		} catch (error) {
			// Superseded by a newer list request — let the newer one own the state.
			if (isAbortError(error)) {
				return
			}
			console.error('Failed to search devices:', error)
			set({
				isLoading: false,
				devices: [],
				totalCount: 0,
				totalPages: 1,
				error: toPageError(error, 'Failed to search devices'),
			})
		} finally {
			inFlightDeviceFetches.delete(requestKey)
		}
	}

	return {
		// Initial State
		devices: [],
		searchFilters: [],
		searchQuery: null,
		tabCounts: { active: 0, inactive: 0, all: 0 },
		deviceFilterOptions: [],
		dashboards: [],
		columns: [],
		deviceView: DEFAULT_DEVICE_VIEW,
		totalCount: 0,
		currentPage: 1,
		pageSize: 20,
		statusFilter: DeviceStatus.All,
		sortBy: 'uid',
		sortOrder: 'asc',
		sortScope: null,
		isLoading: false,
		isLoadingDeviceOptions: false,
		error: null,
		deviceOptionsError: null,
		totalPages: 1,

		fetchDevices: async (silent?: boolean, status?: DeviceStatus) => {
			if (!getTenantFromUrl()) {
				return
			}

			const { currentPage, pageSize, statusFilter, sortBy, sortOrder, deviceView } = get()
			const effectiveStatus = status ?? statusFilter
			const request = resolveDeviceListRequest(effectiveStatus, deviceView)

			const requestKey = `${currentPage}:${pageSize}:${request.status}:${request.includeArchived}:${JSON.stringify(sortBy)}:${sortOrder}`
			if (inFlightDeviceFetches.has(requestKey)) {
				return
			}
			inFlightDeviceFetches.add(requestKey)

			// Abort prior list request — latest-wins.
			deviceListAbortController?.abort()
			deviceListAbortController = new AbortController()
			const signal = deviceListAbortController.signal

			// Silent refresh (auto-poll) keeps the current view; don't flash the loading state.
			set(silent ? { error: null } : { isLoading: true, error: null })

			try {
				const response = await deviceApi.getDevicesV3(
					currentPage,
					pageSize,
					request.status,
					undefined,
					undefined,
					sortBy,
					sortOrder,
					signal,
					request.includeArchived,
				)

				const calculatedTotalPages = response.count > 0 ? Math.ceil(response.count / pageSize) : 1

				if (isPageBeyondTotal(currentPage, response.count, calculatedTotalPages)) {
					inFlightDeviceFetches.delete(requestKey)
					set({ currentPage: calculatedTotalPages })
					await get().fetchDevices(silent, status)
					return
				}

				set({
					devices: response.devices,
					totalCount: response.count,
					isLoading: false,
					totalPages: calculatedTotalPages,
					error: null,
					tabCounts: { ...get().tabCounts, [request.status]: response.count },
				})
			} catch (error) {
				// Superseded by a newer list request — let the newer one own the state.
				if (isAbortError(error)) {
					return
				}
				console.error('Failed to fetch devices:', error)
				set({
					isLoading: false,
					error: toPageError(error, 'Failed to load devices'),
				})
			} finally {
				inFlightDeviceFetches.delete(requestKey)
			}
		},
		// Legacy flat path (dialogs, dashboards table): build the AND-only query and run the shared search,
		// keeping the FilterState[] as the active source of truth.
		searchDevices: async (
			filters: FilterState[],
			page?: number,
			pageSize?: number,
			status?: DeviceStatus,
			silent?: boolean,
		) =>
			runSearch({
				query: buildDeviceSearchFilters(filters),
				keySeed: `search:${JSON.stringify(filters)}`,
				patch: { searchFilters: filters, searchQuery: null },
				filtersChanged: JSON.stringify(get().searchFilters) !== JSON.stringify(filters),
				page,
				pageSize,
				status,
				silent,
			}),

		setSearchFilters: (filters: FilterState[]) => {
			set({ searchFilters: filters })
		},

		// Grouped (OR-capable) path: run a prebuilt query, clearing the flat searchFilters so pagination and
		// polling never run the old AND-only path in parallel. Null query restores the unfiltered list.
		applySearchQuery: async (query, page, pageSize, status, silent) =>
			runSearch({
				query,
				keySeed: `query:${JSON.stringify(query)}`,
				patch: { searchQuery: query, searchFilters: [] },
				filtersChanged: JSON.stringify(get().searchQuery) !== JSON.stringify(query),
				page,
				pageSize,
				status,
				silent,
			}),
		setDashboards: (ids: string[]) => set({ dashboards: ids }),
		setColumns: (columns: string[]) => set({ columns }),

		setDeviceView: (view: DeviceView) => {
			if (get().deviceView === view) {
				return
			}
			// Changing what the list holds changes the row count, so restart at page 1 and re-run
			// whichever view is active — same shape as setPageSize/setSort.
			const { searchFilters, searchQuery } = get()
			set({ deviceView: view, currentPage: 1 })
			if (searchQuery !== null) {
				get().applySearchQuery(searchQuery, 1)
			} else if (searchFilters.length > 0) {
				get().searchDevices(searchFilters, 1)
			} else {
				get().fetchDevices()
			}
		},
		refreshTabCounts: async () => {
			if (!getTenantFromUrl()) {
				return
			}
			if (refreshTabCountsInFlight) {
				return
			}
			refreshTabCountsInFlight = true
			try {
				const { status } = deviceViewRequest(get().deviceView)
				const res = await deviceApi.getDevicesV3(1, 1, status)
				set(state => ({ tabCounts: { ...state.tabCounts, [status]: res.count } }))
			} catch (error) {
				console.error('Failed to refresh tab counts:', error)
			} finally {
				refreshTabCountsInFlight = false
			}
		},

		setPage: (page: number) => {
			const { currentPage, searchFilters, searchQuery } = get()
			if (page === currentPage) {
				return
			}

			set({ currentPage: page })
			if (searchQuery !== null) {
				get().applySearchQuery(searchQuery, page)
			} else if (searchFilters.length > 0) {
				get().searchDevices(searchFilters, page)
			} else {
				get().fetchDevices()
			}
		},

		setPageSize: (size: number) => {
			const { searchFilters, searchQuery } = get()
			set({
				pageSize: size,
				currentPage: 1,
			})
			if (searchQuery !== null) {
				get().applySearchQuery(searchQuery, 1, size)
			} else if (searchFilters.length > 0) {
				get().searchDevices(searchFilters, 1, size)
			} else {
				get().fetchDevices()
			}
		},

		setStatusFilter: (status: DeviceStatus) => {
			const { searchFilters, searchQuery } = get()
			set({
				statusFilter: status,
				currentPage: 1,
			})
			if (searchQuery !== null) {
				get().applySearchQuery(searchQuery, 1, undefined, status)
			} else if (searchFilters.length > 0) {
				get().searchDevices(searchFilters, 1)
			} else {
				get().fetchDevices()
			}
		},

		/** Applies a sort without fetching. Route `beforeLoad` calls it, so the page's first fetch already uses it. */
		restoreSort: (sortBy: DeviceSearchSortBy, sortOrder: SortOrder, scope: string) => {
			const current = get()
			if (
				current.sortScope === scope &&
				current.sortOrder === sortOrder &&
				JSON.stringify(current.sortBy) === JSON.stringify(sortBy)
			) {
				return
			}
			set({ sortBy, sortOrder, sortScope: scope, currentPage: 1 })
		},

		setSort: (sortBy: DeviceSearchSortBy, sortOrder: SortOrder) => {
			const { searchFilters, searchQuery } = get()
			// Sort spans the whole dataset, so jump back to page 1 and refetch from the server.
			set({ sortBy, sortOrder, currentPage: 1 })
			if (searchQuery !== null) {
				get().applySearchQuery(searchQuery, 1)
			} else if (searchFilters.length > 0) {
				get().searchDevices(searchFilters, 1)
			} else {
				get().fetchDevices()
			}
		},

		fetchDeviceFilterOptions: async (tenantId?: string, status: DeviceStatus = DeviceStatus.Active) => {
			set({ isLoadingDeviceOptions: true, deviceOptionsError: null })
			// Keyed under the prefix `invalidateDeviceSuggestions` clears. Tenant and status both change the
			// answer; an omitted tenant resolves to the current one so both call styles share an entry.
			const scope = `${tenantId || getTenantForApiCall()}:${status}`
			try {
				const deviceOptions = await queryClient.fetchQuery({
					queryKey: deviceKeys.filterOptionCounts(scope),
					queryFn: () => deviceApi.getDeviceFilterOptions(tenantId, status),
					staleTime: DEVICE_FILTER_OPTIONS_STALE_TIME_MS,
					// The shared client retries three times; a dialog should report a failure now, not in five seconds.
					retry: false,
				})
				set({ deviceFilterOptions: deviceOptions, isLoadingDeviceOptions: false })
			} catch (error) {
				const errorMessage = extractErrorMessage(error, 'Failed to load device filters')
				set({ deviceOptionsError: errorMessage, isLoadingDeviceOptions: false })
			}
		},

		clearError: () => {
			set({ error: null })
		},

		clearDeviceOptionsError: () => {
			set({ deviceOptionsError: null })
		},

		// Page size is the user's saved preference, not navigation state, so the reset leaves it alone.
		resetPagination: () => {
			set({ currentPage: 1 })
		},
	}
})
